import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Course, CourseEnrollment, Login, Tag } from '../../apis/api-client';
import { coursesClient, reportsClient, tagsClient, usersClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';
import { useEffectiveWorkspaceId } from '../../hooks/useEffectiveWorkspaceId';
import { usePagedList } from '../../hooks/usePagedList';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import PagedTable, { PagedTableColumn } from '../common/PagedTable';
import UserAutocomplete from '../common/UserAutocomplete';
import { CourseAutocomplete, EmptyState, EntityLink, TagAutocomplete } from '../common';
import { FilterIcon, SendIcon, WorkspacesIcon } from '../icons';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Stack,
  TextField,
  Typography,
} from '../ui';
import { daysLabel, statusColor, statusLabel } from '../../utils/reportingHelpers';

export default function ActionQueue() {
  const { enqueueSnackbar } = useSnackbar();
  const effectiveWorkspaceId = useEffectiveWorkspaceId();
  const location = useLocation();

  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  // URL-backed filters (IDs only for object types — chips hydrate after).
  const { getInt, getIntArray, getString, setParams } = useUrlFilters();
  const courseIds = getIntArray('courseIds');
  const userIds = getIntArray('userIds');
  const personTagIds = getIntArray('personTagIds');
  const filterStartFrom = getString('startFrom');
  const filterStartTo = getString('startTo');
  const filterDueFrom = getString('dueFrom');
  const filterDueTo = getString('dueTo');
  const initialQuery = getString('q');

  // Hydrated chip state for the autocompletes — kept in sync with URL IDs.
  const [filterCourses, setFilterCourses] = useState<Course[]>([]);
  const [filterPersons, setFilterPersons] = useState<Login[]>([]);
  const [filterPersonTags, setFilterPersonTags] = useState<Tag[]>([]);

  const paged = usePagedList<CourseEnrollment>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        if (!effectiveWorkspaceId) {
          return { items: [], totalCount: 0, page, pageSize };
        }
        const result = await reportsClient.actionQueueSearch(
          effectiveWorkspaceId,
          userIds.length > 0 ? userIds : undefined,
          courseIds.length > 0 ? courseIds : undefined,
          undefined, // groupIds
          false,     // includeUngrouped
          undefined, // courseTagIds
          personTagIds.length > 0 ? personTagIds : undefined,
          filterStartFrom ? (new Date(filterStartFrom) as any) : undefined,
          filterStartTo ? (new Date(filterStartTo) as any) : undefined,
          filterDueFrom ? (new Date(filterDueFrom) as any) : undefined,
          filterDueTo ? (new Date(filterDueTo) as any) : undefined,
          search || undefined,
          page,
          pageSize
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [effectiveWorkspaceId, userIds, courseIds, personTagIds, filterStartFrom, filterStartTo, filterDueFrom, filterDueTo]
    ),
    deps: [
      effectiveWorkspaceId ?? null,
      userIds.join(','),
      courseIds.join(','),
      personTagIds.join(','),
      filterStartFrom,
      filterStartTo,
      filterDueFrom,
      filterDueTo,
    ],
    initialSearch: initialQuery,
    onSearchChange: (s) => setParams({ q: s || null }),
    onError: () => alert(enqueueSnackbar, 'Failed to load action queue', 'error'),
  });

  // ─── Chip hydration ────────────────────────────────────────────────
  // Each effect runs when its URL ID set changes. Placeholder chips show
  // immediately; real objects swap in as the API returns.

  useEffect(() => {
    const want = new Set(courseIds);
    const have = new Set(filterCourses.map(c => c.id!).filter(Boolean));
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    setFilterCourses(courseIds.map(id => filterCourses.find(c => c.id === id) ?? ({ id, title: `Course #${id}` } as Course)));
    const missing = courseIds.filter(id => !filterCourses.some(c => c.id === id));
    missing.forEach(id => {
      coursesClient.getCourseById(id).then(c => {
        if (c) setFilterCourses(prev => prev.map(p => p.id === id ? c : p));
      }).catch(() => { /* placeholder is good enough */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIds.join(',')]);

  useEffect(() => {
    if (!effectiveWorkspaceId) return;
    const want = new Set(userIds);
    const have = new Set(filterPersons.map(p => p.id!).filter(Boolean));
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    setFilterPersons(userIds.map(id => filterPersons.find(p => p.id === id) ?? ({ id } as Login)));
    if (userIds.length > 0) {
      usersClient.searchUsersPaged(undefined, undefined, undefined, undefined, 1, userIds.length, userIds)
        .then(res => {
          const fetched = res.items ?? [];
          setFilterPersons(prev => prev.map(p => fetched.find(f => f.id === p.id) ?? p));
        }).catch(() => { /* placeholder is good enough */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds.join(','), effectiveWorkspaceId]);

  useEffect(() => {
    const want = new Set(personTagIds);
    const have = new Set(filterPersonTags.map(t => t.id!).filter(Boolean));
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    setFilterPersonTags(personTagIds.map(id => filterPersonTags.find(t => t.id === id) ?? ({ id, name: `Tag #${id}` } as Tag)));
    if (personTagIds.length > 0) {
      tagsClient.searchTags(undefined, undefined, undefined, false, false, personTagIds, 1, personTagIds.length)
        .then(res => {
          const fetched = res.items ?? [];
          setFilterPersonTags(prev => prev.map(p => fetched.find(t => t.id === p.id) ?? p));
        }).catch(() => { /* placeholder is good enough */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personTagIds.join(',')]);

  // Workspace switch: clear workspace-scoped filters (group / student / course).
  // personTags + dates carry across.
  const prevWsIdRef = useRef(effectiveWorkspaceId);
  useEffect(() => {
    if (prevWsIdRef.current != null && prevWsIdRef.current !== effectiveWorkspaceId) {
      setParams({ userIds: null, courseIds: null });
    }
    prevWsIdRef.current = effectiveWorkspaceId;
  }, [effectiveWorkspaceId, setParams]);

  const activeFilterCount = [
    filterCourses.length > 0,
    filterPersons.length > 0,
    filterStartFrom || filterStartTo,
    filterDueFrom || filterDueTo,
    filterPersonTags.length > 0,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setParams({
      courseIds: null, userIds: null, personTagIds: null,
      startFrom: null, startTo: null, dueFrom: null, dueTo: null,
    });
  };

  const handleSendReminder = async (row: CourseEnrollment) => {
    if (!row.userId || !row.assignmentDefinitionId) return;
    const key = `${row.userId}-${row.assignmentDefinitionId}`;
    setSendingIds(prev => new Set(prev).add(key));
    try {
      await reportsClient.sendReminder({ userId: row.userId, assignmentDefinitionId: row.assignmentDefinitionId });
      alert(enqueueSnackbar, 'Reminder sent', 'success');
    } catch {
      alert(enqueueSnackbar, 'Failed to send reminder', 'error');
    } finally {
      setSendingIds(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Capture current URL so entity-link state restores filters on Back.
  const fromUrl = `${location.pathname}${location.search}`;
  const entityState = { from: fromUrl, fromText: 'Back to Action Queue' };

  const columns: PagedTableColumn<CourseEnrollment>[] = [
    {
      key: 'student',
      label: 'Student',
      render: (row) => (
        <EntityLink
          to={row.userId != null ? `/students/${row.userId}` : null}
          tooltip="Open student profile"
          state={entityState}
        >
          <Typography variant="body2" fontWeight={500}>
            {row.lastName}, {row.firstName}
          </Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
        </EntityLink>
      ),
    },
    {
      key: 'course',
      label: 'Course',
      render: (row) => (
        <EntityLink
          to={row.courseId != null ? `/course/${row.courseId}` : null}
          tooltip="Open course"
          state={entityState}
        >
          <span>{row.courseTitle}</span>
        </EntityLink>
      ),
    },
    {
      key: 'startDate',
      label: 'Start Date',
      render: (row) => row.startDate ? new Date(row.startDate).toLocaleDateString() : '—',
    },
    {
      key: 'deadline',
      label: 'Deadline',
      render: (row) => row.deadline ? new Date(row.deadline).toLocaleDateString() : '—',
    },
    {
      key: 'daysLeft',
      label: 'Days Left',
      render: (row) => {
        const { text, isOverdue } = daysLabel(row.deadline);
        return (
          <Typography variant="body2" color={isOverdue ? 'error.main' : 'text.primary'}>
            {text}
          </Typography>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <Chip
          label={statusLabel(row.assignmentStatus ?? null)}
          size="small"
          sx={{ bgcolor: statusColor(row.assignmentStatus ?? null) }}
        />
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (row) => {
        const sendKey = `${row.userId}-${row.assignmentDefinitionId}`;
        const isSending = sendingIds.has(sendKey);
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleSendReminder(row)}
            disabled={isSending}
            startIcon={isSending ? <CircularProgress size={14} /> : <SendIcon />}
          >
            Remind
          </Button>
        );
      },
    },
  ];

  if (!effectiveWorkspaceId) {
    return (
      <EmptyState
        icon={<WorkspacesIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
        title="Select a workspace to view this report"
        subtitle="Use the workspace switcher in the sidebar to choose a workspace"
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Button
          size="small"
          variant={filtersOpen ? 'contained' : 'outlined'}
          color={activeFilterCount > 0 ? 'primary' : 'inherit'}
          onClick={() => setFiltersOpen(v => !v)}
          startIcon={<FilterIcon />}
        >
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
        {activeFilterCount > 0 && (
          <Button size="small" variant="text" color="inherit" onClick={clearAllFilters}>
            Clear filters
          </Button>
        )}
      </Stack>

      <Collapse in={filtersOpen}>
        <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <CourseAutocomplete
                multiple
                value={filterCourses}
                onChange={(v) => {
                  const next = (v as Course[]) ?? [];
                  setFilterCourses(next);
                  setParams({ courseIds: next.map(c => c.id!).filter(Boolean) });
                }}
                label="Course"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <UserAutocomplete
                multiple
                value={filterPersons}
                onChange={(v) => {
                  const next = (v as Login[]) ?? [];
                  setFilterPersons(next);
                  setParams({ userIds: next.map(p => p.id!).filter(Boolean) });
                }}
                workspaceId={effectiveWorkspaceId}
                label="Person"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TagAutocomplete
                multiple
                value={filterPersonTags}
                onChange={(v) => {
                  const next = (v as Tag[]) ?? [];
                  setFilterPersonTags(next);
                  setParams({ personTagIds: next.map(t => t.id!).filter(Boolean) });
                }}
                workspaceId={effectiveWorkspaceId ?? undefined}
                includeAccountWide
                label="Person Tags"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Start From" type="date" size="small" fullWidth
                value={filterStartFrom}
                onChange={e => setParams({ startFrom: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Start To" type="date" size="small" fullWidth
                value={filterStartTo}
                onChange={e => setParams({ startTo: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Due From" type="date" size="small" fullWidth
                value={filterDueFrom}
                onChange={e => setParams({ dueFrom: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Due To" type="date" size="small" fullWidth
                value={filterDueTo}
                onChange={e => setParams({ dueTo: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <PagedTable<CourseEnrollment>
        columns={columns}
        rows={paged.items}
        totalCount={paged.totalCount}
        page={paged.page}
        pageSize={paged.pageSize}
        loading={paged.loading}
        rowKey={(r) => `${r.userId}-${r.assignmentDefinitionId}-${r.enrollmentId ?? 'none'}`}
        onPageChange={paged.setPage}
        onPageSizeChange={paged.setPageSize}
        search={paged.search}
        searchPlaceholder="Search by name, email, or course…"
        onSearchChange={paged.setSearch}
        emptyMessage="No active or overdue assignments match your filters"
      />
    </Box>
  );
}
